import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Header() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  const [guidesDropdownOpen, setGuidesDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = () => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    setDropdownOpen(true);
  };
  const handleMouseLeave = () => {
    closeTimer.current = setTimeout(() => { setDropdownOpen(false); }, 200);
  };
  const handleGuidesEnter = () => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    setGuidesDropdownOpen(true);
  };
  const handleGuidesLeave = () => {
    closeTimer.current = setTimeout(() => { setGuidesDropdownOpen(false); }, 200);
  };

  const articleSlugs = [
    { slug: "pandabuy-sheet-complete-guide", label: "Ultimate Shopping Guide" },
    { slug: "best-pandabuy-spreadsheet-sneakers-2026", label: "Best Sneakers 2026" },
    { slug: "how-to-use-pandabuy-spreadsheets", label: "How to Use Spreadsheets" },
    { slug: "pandabuy-spreadsheet-safety-tips", label: "Safety Tips" },
    { slug: "pandabuy-spreadsheet-fashion-trends", label: "Fashion Trends 2026" },
    { slug: "pandabuy-spreadsheet-beginner-guide", label: "Beginner Guide" },
    { slug: "pandabuy-spreadsheet-accessories-guide", label: "Accessories Guide" },
    { slug: "pandabuy-spreadsheet-quality-comparison", label: "Quality Comparison" },
    { slug: "pandabuy-spreadsheet-daily-updates", label: "Daily Updates" },
    { slug: "pandabuy-spreadsheet-vs-traditional-shopping", label: "vs Traditional Shopping" },
  ];

  return (
    <header className="fixed top-0 left-0 w-full z-50 bg-background-50 border-b border-background-200/70" style={{ height: "72px" }}>
      <nav className="h-full flex items-center justify-between px-6 md:px-10 max-w-[1440px] mx-auto">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 text-foreground-950 hover:opacity-80 transition-opacity z-20 shrink-0">
          <span className="font-heading text-xl md:text-2xl font-bold tracking-tight whitespace-nowrap">
            pandabuy<span className="text-accent-500">.</span>rest
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-6 lg:gap-8">
          <Link to="/" className="text-sm font-semibold text-foreground-950 hover:text-accent-500 transition-colors whitespace-nowrap">
            Home
          </Link>

          {/* Categories Dropdown */}
          <div ref={dropdownRef} className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <button className="flex items-center gap-1 text-sm font-semibold text-foreground-950 hover:text-accent-500 transition-colors whitespace-nowrap cursor-pointer">
              Categories
              <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}></i>
            </button>
            {dropdownOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-background-50 rounded-xl border border-background-200/70 py-2 min-w-[220px] z-50">
                {categories.map((cat) => (
                  <a key={cat.name} href={cat.url} target="_blank" rel="noopener nofollow" className="block px-5 py-2 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap">
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Shopping Guide Dropdown */}
          <div className="relative" onMouseEnter={handleGuidesEnter} onMouseLeave={handleGuidesLeave}>
            <button className="flex items-center gap-1 text-sm font-semibold text-foreground-950 hover:text-accent-500 transition-colors whitespace-nowrap cursor-pointer">
              Shopping Guide
              <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${guidesDropdownOpen ? "rotate-180" : ""}`}></i>
            </button>
            {guidesDropdownOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-background-50 rounded-xl border border-background-200/70 py-2 min-w-[240px] z-50">
                {articleSlugs.map((a) => (
                  <Link key={a.slug} to={`/article/${a.slug}`} className="block px-5 py-2 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap">
                    {a.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Desktop CTA Buttons */}
        <div className="hidden md:flex items-center gap-3 shrink-0">
          <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center px-5 py-2 text-sm font-semibold rounded-full border-2 border-foreground-950 text-foreground-950 hover:bg-foreground-950 hover:text-background-50 transition-all duration-200 whitespace-nowrap cursor-pointer">
            Pandabuy Sheet
          </a>
          <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center px-5 py-2 text-sm font-semibold rounded-full bg-foreground-950 text-background-50 hover:bg-foreground-800 transition-all duration-200 whitespace-nowrap cursor-pointer">
            Buy It Now
          </a>
        </div>

        {/* Hamburger */}
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg text-foreground-950 hover:bg-background-100 transition-colors z-20 cursor-pointer" aria-label="Toggle menu">
          <i className={`text-xl ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
        </button>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 top-[72px] bg-background-50 z-40 overflow-y-auto">
          <div className="px-6 py-6 space-y-1">
            <Link to="/" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 text-base font-semibold text-foreground-950 py-3.5 border-b border-background-200/70">
              <i className="ri-home-4-line text-lg text-accent-500 w-6 h-6 flex items-center justify-center"></i>
              Home
            </Link>
            <div>
              <button onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)} className="flex items-center justify-between w-full text-base font-semibold text-foreground-950 py-3.5 border-b border-background-200/70 cursor-pointer">
                <span className="flex items-center gap-3">
                  <i className="ri-grid-line text-lg text-accent-500 w-6 h-6 flex items-center justify-center"></i>
                  Categories
                </span>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileDropdownOpen ? "rotate-180" : ""}`}></i>
              </button>
              {mobileDropdownOpen && (
                <div className="py-2 grid grid-cols-2 gap-1">
                  {categories.map((cat) => (
                    <a key={cat.name} href={cat.url} target="_blank" rel="noopener nofollow" onClick={() => { setMobileDropdownOpen(false); setMobileMenuOpen(false); }} className="block px-3 py-2.5 text-sm text-foreground-600 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors whitespace-nowrap truncate">
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
            <div>
              <button onClick={() => setGuidesDropdownOpen(!guidesDropdownOpen)} className="flex items-center justify-between w-full text-base font-semibold text-foreground-950 py-3.5 border-b border-background-200/70 cursor-pointer">
                <span className="flex items-center gap-3">
                  <i className="ri-book-open-line text-lg text-accent-500 w-6 h-6 flex items-center justify-center"></i>
                  Shopping Guide
                </span>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${guidesDropdownOpen ? "rotate-180" : ""}`}></i>
              </button>
              {guidesDropdownOpen && (
                <div className="py-2 space-y-1">
                  {articleSlugs.map((a) => (
                    <Link key={a.slug} to={`/article/${a.slug}`} onClick={() => { setGuidesDropdownOpen(false); setMobileMenuOpen(false); }} className="block px-3 py-2.5 text-sm text-foreground-600 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors">
                      {a.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <div className="flex flex-col gap-3 pt-4">
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center justify-center px-5 py-3 text-sm font-semibold rounded-full border-2 border-foreground-950 text-foreground-950 hover:bg-foreground-950 hover:text-background-50 transition-all duration-200 whitespace-nowrap cursor-pointer">
                Pandabuy Sheet <i className="ri-arrow-right-up-line ml-2"></i>
              </a>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center justify-center px-5 py-3 text-sm font-semibold rounded-full bg-foreground-950 text-background-50 hover:bg-foreground-800 transition-all duration-200 whitespace-nowrap cursor-pointer">
                Buy It Now <i className="ri-arrow-right-line ml-2"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}